/* jshint esversion: 6 */
import Q from 'q';
import { map as Qmap } from 'q-map-throttle-ll';
import {
  uploadViaBeamr,
  suitableBeamrFile,
  shouldDisableBeamr,
  disableBeamr,
  uploadCompressed,
} from '../beamr/beamr';
import UploadAnalytics from '../upload-analytics';
import UploadTimings from '../upload-timings';

import showLoginOverlay from '../utils/show-login-overlay';

Q.map = Qmap;

const GenericPaginatedUploadMixin = {
  defaultWindowSize: 6,
  defaultRetryAttempts: 5,

  getInitialState() {
    return {
      uploadPaused: false,
    };
  },
  UNSAFE_componentWillMount() {
    this.uploadPaused = null;
  },

  performUpload(
    uploadUrl = this.params && this.params.uploadUrl,
    additionalData = this.params && this.params.additionalData,
    onUploaded = this.params && this.params.onUploaded,
    onUploadError = this.params && this.params.onUploadError,
  ) {
    // remember the latest params so that restart will work
    this.params || (this.params = {});
    this.params.uploadUrl = uploadUrl;
    this.params.additionalData = additionalData;
    this.params.onUploaded = onUploaded;
    this.params.onUploadError = onUploadError;

    const uploadTransactionId = generateId('fup');
    const beamrAttempts = {};

    // break up array of photos into Windows
    // so as not to swamp the server
    const component = this;
    const windowSize = this.windowSize || this.defaultWindowSize;
    const retryAttempts = this.retryAttempts || this.defaultRetryAttempts;
    const files = component.filesSelected().filter((file) => !file.uploaded);
    const fileUploadSource =
      additionalData?.uploadSource || component.props.service;
    const browserCompressionEnabled =
      !!window.features.browserImageCompression == true;
    const beamrEnabled = !!window.beamrCompressionEnabled == true;
    const tempUploadTimings = new UploadTimings();
    var browserCompressionFailed = false;

    tempUploadTimings.started();

    // TODO: Extrapolate the compression/files loops into their own functions
    if (
      browserCompressionEnabled &&
      !beamrEnabled &&
      !browserCompressionFailed
    ) {
      try {
        const compressedFiles = files.map(async (file) => {
          var img = new Image();
          img.src = URL.createObjectURL(file);
          await img.decode();

          const canvas = new OffscreenCanvas(img.width, img.height);
          canvas.getContext('2d').drawImage(img, 0, 0);

          var mimeType = 'image/jpeg';
          const compressFile = await canvas
            .convertToBlob({
              type: mimeType,
              quality: Number(window.features?.browserCompressionLevel) || 0.5,
            })
            .then((blob) => {
              return new File([blob], file.name);
            });

          compressFile.service = file.service;
          compressFile.attempts = 0;
          compressFile.uploadTimings = tempUploadTimings;
          compressFile.uploadAnalytics = new UploadAnalytics({
            fileName: compressFile.name,
            uploadTransactionId,
            uploadTimings: compressFile.uploadTimings,
            fileUploadSource,
          });
          compressFile.uploadTimings.compressed();
          compressFile.uploadAnalytics.setFileSizes(
            file.size,
            compressFile.size,
          );
          component.files[component.files.indexOf(file)] = compressFile;
          return compressFile;
        });

        Q.map(compressedFiles, sendFile, windowSize);
      } catch (error) {
        browserCompressionFailed = true;
        console.error('browserCompressionFailed: ', error);
      }
    } else {
      files.forEach((f) => {
        f.attempts = 0;
        f.beamrId = generateId('f');
        f.beamrFailed = false;
        f.uploadTimings = tempUploadTimings;
        f.uploadAnalytics = new UploadAnalytics({
          fileName: f.name,
          uploadTransactionId,
          uploadTimings: f.uploadTimings,
          fileUploadSource,
        });
        f.uploadAnalytics.setFileSizes(f.size);
      });

      Q.map(files, sendFile, windowSize);
    }

    function sendFile(file) {
      const deferred = Q.defer();
      let formData;

      if (component.paused()) {
        deferred.reject();
        return deferred.promise;
      }

      // TODO: Complete the compressedFiles promises to turn this into
      // a singular command that can be generalized
      if (browserCompressionEnabled && !beamrEnabled) {
        file.then((compressFile) => {
          formData = buildFormData(compressFile, additionalData);
          _send(compressFile, formData);
        });
      } else {
        formData = buildFormData(file, additionalData);
        _send(file, formData);
      }

      return deferred.promise;

      function _send(file, data) {
        if (component.paused()) {
          deferred.reject();
          return;
        }

        if (file.beamrFailed) {
          file.uploadTimings.fellBack();
          console.debug(
            `name:${file.name} `,
            'Upload fallback starting...',
            file.uploadTimings,
          );
        }

        if (uploadViaBeamr() && suitableBeamrFile(file) && beamrEnabled) {
          console.debug(file.name, file.beamrId, 'Uploading file via Beamr');
          file.uploadAnalytics.beamr_upload_source = 'beamr';
          uploadCompressed(uploadUrl, file, additionalData, onBeamrDone);
        } else {
          if (browserCompressionEnabled) {
            file.uploadAnalytics.beamr_upload_source = 'local-compression';
          } else {
            file.uploadAnalytics.beamr_upload_source = 'raw';
          }
          console.debug(file.name, file.beamrId, 'Uploading file via File');
          if (
            typeof component.makesTooManyRequests === 'function' &&
            component.makesTooManyRequests(file.service)
          ) {
            onUploadError(
              `${
                file.service.charAt(0).toUpperCase() + file.service.slice(1)
              } is currently unavailable, please use other service to upload your photos`,
            );
            deferred.resolve();
            return;
          }

          $.ajax(uploadUrl, {
            data,
            processData: false,
            contentType: false,
            type: 'POST',
          })
            .done((data, _textStatus, _jqXHR) => {
              onDone(data, file);
            })
            .fail((jqXHR, _textStatus, _errorThrown) => {
              onFail(jqXHR, file);
            });
        }
      }

      function onBeamrDone(didWork, data, event) {
        console.debug('onBeamrDone', event);

        const fileData = event.file.jmu_user_data || {},
          fileId = fileData.beamrId,
          compressedFile = files.find((f) => f.beamrId == fileId);

        console.debug('onBeamrDone for file:', compressedFile, files);

        if (didWork) {
          onDone(data, compressedFile);
        } else {
          onBeamrFail(compressedFile);
        }
      }

      function onBeamrFail(file) {
        console.debug(
          `name:${file.name} `,
          'Beamr failed.',
          file.uploadTimings,
        );

        if (!file.beamrFailed) {
          file.beamrFailed = true;

          if (shouldDisableBeamr(files, beamrAttempts)) {
            disableBeamr();
          }

          _send(file, buildFormData(file, additionalData));
        }
      }

      function onDone(data, file) {
        if (typeof window.newrelic !== 'undefined') {
          window.newrelic.setCustomAttribute('uploadSourceData', {
            data,
            file,
          });
        }
        const imageData =
          (data?.result || {}).image || (data?.result || {}).images;

        file.uploaded = true;
        file.uploadTimings.uploaded();
        if (!file.uploadTimings.compression || file.uploadTimings.fallback) {
          console.debug(
            `name:${file.name} `,
            'Uploaded without compression.',
            file.uploadTimings,
          );
        }

        onUploaded(imageData, component.props.service);
        deferred.resolve();

        file.uploadTimings.completed((uploadTimings) => {
          if (uploadTimings.compression > uploadTimings.upload) {
            console.error(
              '[Beamr Stats] Compression time greater than e2e time:',
              uploadTimings,
              file,
            );
          }
        });

        file.uploadAnalytics.sendObservability();

        console.debug(`name:${file.name} `, 'Done!', file.uploadTimings);
      }

      function onFail(jqXHR, file) {
        console.debug(
          `name:${file.name} `,
          'Upload failed.',
          file.uploadTimings,
        );

        if (jqXHR && (jqXHR.status === 401 || jqXHR.status_code === 401)) {
          // not authorized
          this.pause();
          deferred.resolve();

          showLoginOverlay().done(() => {
            component.restart();
          });

          return;
        }

        if (
          typeof component.reachedErrorLimit === 'function' &&
          component.reachedErrorLimit(file.service)
        ) {
          // cut off retries
          onUploadError();
          deferred.resolve();
          return;
        }

        if (typeof component.saveErrors === 'function') {
          component.saveErrors(file.service, jqXHR.status || jqXHR.status_code);
        }

        if (file.attempts++ < retryAttempts) {
          _send(file, buildFormData(file, additionalData));
          return;
        }

        onUploadError();
        deferred.resolve();
      }
    }

    function buildFormData(file, additionalData) {
      const formData = new FormData();
      const { uploadSource: uploadSourceOverride, ...otherParams } =
        additionalData;
      const uploadSource = uploadSourceOverride || component.props.service;

      formData.set('source', uploadSource);
      Object.entries(otherParams).forEach(([key, value]) => {
        formData.append(key, value);
      });

      component._buildFormData(formData, file);

      return formData;
    }

    function generateId(prefix) {
      function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
          .toString(16)
          .substring(1);
      }
      return `${prefix}${s4()}${s4()}${s4()}${s4()}`;
    }
  },

  pause() {
    this.setState({
      uploadPaused: true,
    });
  },
  restart() {
    this.setState(
      {
        uploadPaused: false,
      },
      function () {
        this.performUpload();
      },
    );
  },
  paused() {
    return this.state.uploadPaused;
  },
};

export default GenericPaginatedUploadMixin;
